import { toDate } from '../../utils/toDate'
import { DataTableAlign, DataTableColumn } from './DataTable.types'

export type ColumnOverrides = { align?: DataTableAlign; width?: number; tooltip?: string }

const toTime = (value?: Date | string | null) => toDate(value)?.getTime()

export const textColumn = <TRow, TColumnId extends string>(
    id: TColumnId,
    label: string,
    getValue: (row: TRow) => string | undefined,
    overrides?: ColumnOverrides
): DataTableColumn<TRow, TColumnId> => ({ id, label, getSortValue: getValue, ...overrides })

/** Takes a Date or the string a record stores, so a table need not parse before it sorts. */
export const dateColumn = <TRow, TColumnId extends string>(
    id: TColumnId,
    label: string,
    getValue: (row: TRow) => Date | string | null | undefined,
    overrides?: ColumnOverrides
): DataTableColumn<TRow, TColumnId> => ({
    id,
    label,
    align: 'center',
    getSortValue: (row) => toTime(getValue(row)),
    ...overrides,
})

export const countColumn = <TRow, TColumnId extends string>(
    id: TColumnId,
    label: string,
    getValue: (row: TRow) => number,
    overrides?: ColumnOverrides
): DataTableColumn<TRow, TColumnId> => ({ id, label, align: 'center', getSortValue: getValue, ...overrides })

/** Sorts on the negated value, so Yes comes before No. */
export const yesNoColumn = <TRow, TColumnId extends string>(
    id: TColumnId,
    label: string,
    getValue: (row: TRow) => boolean,
    overrides?: ColumnOverrides
): DataTableColumn<TRow, TColumnId> => ({
    id,
    label,
    align: 'center',
    getSortValue: (row) => !getValue(row),
    ...overrides,
})

type ColumnBuilder<TRow, TColumnId extends string, TValue> = (
    id: TColumnId,
    label: string,
    getValue: (row: TRow) => TValue,
    overrides?: ColumnOverrides
) => DataTableColumn<TRow, TColumnId>

export const createColumnBuilders = <TRow, TColumnId extends string>() => {
    const text: ColumnBuilder<TRow, TColumnId, string | undefined> = textColumn
    const date: ColumnBuilder<TRow, TColumnId, Date | string | null | undefined> = dateColumn
    const count: ColumnBuilder<TRow, TColumnId, number> = countColumn
    const yesNo: ColumnBuilder<TRow, TColumnId, boolean> = yesNoColumn

    return { text, date, count, yesNo }
}
